import { DEFAULT_SPACE, isKnownSpace, SPACE_CONFIG } from '@/constants/spaces'
import { isLocalOrDev } from '@/utils/env'
import type { SpaceInfo, SpaceKey } from '@/api/types'
import type { SpaceFeature } from '@/constants/spaces'
import type {
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
} from 'vue-router'

export const useSpaces = createGlobalState(() => {
  const spaces = ref<SpaceInfo[]>([])

  const hasMultipleSpaces = computed(() => spaces.value.length > 1)

  function setSpaces(newSpaces: SpaceInfo[] = []) {
    spaces.value = newSpaces.filter(
      (space) =>
        isKnownSpace(space.key) &&
        (!SPACE_CONFIG[space.key].devOnly || isLocalOrDev()),
    )
  }

  function isSpaceAllowed(key: SpaceKey, feature?: SpaceFeature) {
    const allowed =
      key === DEFAULT_SPACE || spaces.value.some((space) => space.key === key)
    return allowed && (!feature || SPACE_CONFIG[key].features.includes(feature))
  }

  return {
    spaces,
    hasMultipleSpaces,
    setSpaces,
    isSpaceAllowed,
  }
})

// '' instead of undefined, otherwise vue-router re-inherits the current space param
export function toSpaceParam(key: SpaceKey) {
  return key === DEFAULT_SPACE ? '' : key
}

export function getSpaceFromParams(params: object): SpaceKey {
  const space = (params as { space?: unknown }).space
  return isKnownSpace(space) ? space : DEFAULT_SPACE
}

// routes outside of [[space]] (e.g. /ops) declare their space in meta.space
export function getSpaceFromRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'params' | 'meta'>,
): SpaceKey {
  return isKnownSpace(route.meta.space)
    ? route.meta.space
    : getSpaceFromParams(route.params)
}

export function useCurrentSpace() {
  const route = useRoute()
  const router = useRouter()
  const { locale } = useI18n()
  const { spaces } = useSpaces()

  const currentSpace = computed(() => getSpaceFromRoute(route))
  const currentSpaceConfig = computed(() => SPACE_CONFIG[currentSpace.value])
  const currentSpaceParam = computed(() => toSpaceParam(currentSpace.value))
  const currentSpaceInfo = computed(() =>
    spaces.value.find((space) => space.key === currentSpace.value),
  )

  function getSpaceLabel(space: SpaceInfo) {
    return space.labels[locale.value] ?? space.label
  }

  // the feature root (record declaring meta.feature) of the current route in the given space, otherwise the space's home
  function getSpaceSwitchPath(key: SpaceKey) {
    const featureRecord = route.matched.find((record) => record.meta.feature)
    const feature = featureRecord?.meta.feature as SpaceFeature | undefined
    const name =
      featureRecord?.name &&
      feature &&
      SPACE_CONFIG[key].features.includes(feature)
        ? featureRecord.name.toString()
        : SPACE_CONFIG[key].home

    return router.resolve({
      name,
      params: name.includes('[[space]]') ? { space: toSpaceParam(key) } : {},
    } as RouteLocationRaw).path
  }

  return {
    currentSpace,
    currentSpaceConfig,
    currentSpaceParam,
    currentSpaceInfo,
    getSpaceLabel,
    getSpaceSwitchPath,
  }
}
